import WorkspaceService from '@/services/WorkspaceService';
import { notify } from '@/utils/notifications';

/**
 * Initial state for workspace module
 */
const initialState = {
    // Current workspace data
    currentWorkspace: null,

    // All user workspaces
    userWorkspaces: [],

    // Workspace members for current workspace
    members: [],

    // Loading states
    loading: {
        workspaces: false,
        currentWorkspace: false,
        members: false,
        switching: false,
        initializing: false
    },

    // Error state
    error: null,

    // Last updated timestamp
    lastUpdated: null,

    // Initialization state to prevent duplicates
    isInitializing: false,
    isInitialized: false
};

/**
 * Workspace module for Vuex store
 *
 * Manages workspace data, user workspace access, workspace switching,
 * and workspace member management
 */
export const workspace = {
    namespaced: true,
    state: { ...initialState },

    mutations: {
        /**
         * Set loading state for specific operation
         *
         * @param {Object} state - Current state
         * @param {Object} payload - Loading operation and status
         */
        SET_LOADING(state, { operation, isLoading }) {
            state.loading[operation] = isLoading;
        },

        /**
         * Set error state
         *
         * @param {Object} state - Current state
         * @param {Error} error - Error object
         */
        SET_ERROR(state, error) {
            state.error = error;
            state.lastUpdated = new Date();
        },

        /**
         * Clear error state
         *
         * @param {Object} state - Current state
         */
        CLEAR_ERROR(state) {
            state.error = null;
        },

        /**
         * Set initialization state
         *
         * @param {Object} state - Current state
         * @param {boolean} isInitializing - Initialization status
         */
        SET_INITIALIZING(state, isInitializing) {
            state.isInitializing = isInitializing;
        },

        /**
         * Set initialized state
         *
         * @param {Object} state - Current state
         * @param {boolean} isInitialized - Initialized status
         */
        SET_INITIALIZED(state, isInitialized) {
            state.isInitialized = isInitialized;
        },

        /**
         * Set current workspace
         *
         * @param {Object} state - Current state
         * @param {Object} workspace - Workspace object
         */
        SET_CURRENT_WORKSPACE(state, workspace) {
            state.currentWorkspace = workspace;
            state.error = null;
            state.lastUpdated = new Date();

            // Store in localStorage for persistence
            if (workspace) {
                localStorage.setItem('currentWorkspaceId', workspace.id);
            } else {
                localStorage.removeItem('currentWorkspaceId');
            }
        },

        /**
         * Set user workspaces list
         *
         * @param {Object} state - Current state
         * @param {Array} workspaces - List of user workspaces
         */
        SET_USER_WORKSPACES(state, workspaces) {
            state.userWorkspaces = workspaces;
            state.error = null;
            state.lastUpdated = new Date();
        },

        /**
         * Add a new workspace to user workspaces
         *
         * @param {Object} state - Current state
         * @param {Object} workspace - New workspace object
         */
        ADD_WORKSPACE(state, workspace) {
            state.userWorkspaces.push(workspace);
            state.lastUpdated = new Date();
        },

        /**
         * Update a workspace in the user workspaces list
         *
         * @param {Object} state - Current state
         * @param {Object} updatedWorkspace - Updated workspace object
         */
        UPDATE_WORKSPACE(state, updatedWorkspace) {
            const index = state.userWorkspaces.findIndex(w => w.id === updatedWorkspace.id);
            if (index !== -1) {
                state.userWorkspaces.splice(index, 1, updatedWorkspace);
            }

            // Update current workspace if it's the same
            if (state.currentWorkspace && state.currentWorkspace.id === updatedWorkspace.id) {
                state.currentWorkspace = updatedWorkspace;
            }

            state.lastUpdated = new Date();
        },

        /**
         * Remove a workspace from user workspaces
         *
         * @param {Object} state - Current state
         * @param {string} workspaceId - ID of workspace to remove
         */
        REMOVE_WORKSPACE(state, workspaceId) {
            state.userWorkspaces = state.userWorkspaces.filter(w => w.id !== workspaceId);

            // Clear current workspace if it's the removed one
            if (state.currentWorkspace && state.currentWorkspace.id === workspaceId) {
                state.currentWorkspace = null;
                localStorage.removeItem('currentWorkspaceId');
            }

            state.lastUpdated = new Date();
        },

        /**
         * Set workspace members
         *
         * @param {Object} state - Current state
         * @param {Array} members - List of workspace members
         */
        SET_MEMBERS(state, members) {
            state.members = members;
            state.error = null;
            state.lastUpdated = new Date();
        },

        /**
         * Add a member to workspace
         *
         * @param {Object} state - Current state
         * @param {Object} member - New member object
         */
        ADD_MEMBER(state, member) {
            state.members.push(member);
            state.lastUpdated = new Date();
        },

        /**
         * Update a workspace member
         *
         * @param {Object} state - Current state
         * @param {Object} updatedMember - Updated member object
         */
        UPDATE_MEMBER(state, updatedMember) {
            const index = state.members.findIndex(m => m.userId === updatedMember.userId);
            if (index !== -1) {
                state.members.splice(index, 1, updatedMember);
            }
            state.lastUpdated = new Date();
        },

        /**
         * Remove a member from workspace
         *
         * @param {Object} state - Current state
         * @param {number} userId - ID of user to remove
         */
        REMOVE_MEMBER(state, userId) {
            state.members = state.members.filter(m => m.userId !== userId);
            state.lastUpdated = new Date();
        },

        /**
         * Reset workspace state to initial values
         *
         * @param {Object} state - Current state
         */
        RESET_STATE(state) {
            Object.assign(state, { ...initialState });
            localStorage.removeItem('currentWorkspaceId');
        }
    },

    actions: {
        /**
         * Initialize workspace state
         * This should be called on app startup to restore workspace state
         *
         * @param {Object} context - Vuex context
         * @param {Object} options - Options object with silent flag
         */
        async initialize({ dispatch, rootGetters, state, commit }, options = {}) {
            const { silent = false, forceDefaultWorkspace = false } = options;
            
            // Only initialize if user is logged in
            if (!rootGetters['auth/isLoggedIn']) {
                console.log('⚠️ Workspace initialization skipped - user not logged in');
                return;
            }

            // Prevent duplicate initialization calls
            if (state.loading.initializing) {
                console.log('Workspace initialization already in progress, skipping duplicate call');
                return;
            }

            commit('SET_LOADING', { operation: 'initializing', isLoading: true });

            try {
                // Only clear current workspace on fresh login (not on workspace switches)
                if (forceDefaultWorkspace) {
                    console.log('🔄 Fresh login detected, clearing workspace for default selection');
                    commit('SET_CURRENT_WORKSPACE', null);
                }
                
                // Load user workspaces (use fetchMyWorkspaces to get isDefault field)
                await dispatch('fetchMyWorkspaces');

                // Only set workspace if there isn't already a current workspace
                if (!state.currentWorkspace) {
                    // Priority order: 1) Default workspace, 2) Last selected workspace, 3) First workspace
                    
                    // First, try to find and use the user's default workspace
                    const defaultWorkspace = state.userWorkspaces.find(w => w.default === true);
                    
                    if (defaultWorkspace) {
                        console.log('🏠 Using user default workspace:', defaultWorkspace.name);
                        await dispatch('setCurrentWorkspace', { workspaceId: defaultWorkspace.id, silent });
                    } else {
                        // Fallback to last selected workspace from localStorage
                        const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
                        if (savedWorkspaceId) {
                            console.log('💾 Using last selected workspace from localStorage:', savedWorkspaceId);
                            await dispatch('setCurrentWorkspace', { workspaceId: savedWorkspaceId, silent });
                        } else {
                            // Final fallback: Set first workspace as current
                            console.log('📋 Using first available workspace as fallback');
                            await dispatch('setFirstWorkspaceAsDefault', { silent });
                        }
                    }
                }

                console.log('✅ Workspace initialization completed');
                commit('SET_INITIALIZED', true);
            } catch (error) {
                console.error('Error initializing workspace state:', error);
            } finally {
                commit('SET_LOADING', { operation: 'initializing', isLoading: false });
            }
        },

        /**
         * Fetch all user workspaces
         *
         * @param {Object} context - Vuex context
         */
        async fetchUserWorkspaces({ commit }) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.getUserWorkspaces();
                commit('SET_USER_WORKSPACES', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error);
                console.error('Error fetching user workspaces:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        },

        /**
         * Set current workspace by ID
         *
         * @param {Object} context - Vuex context
         * @param {string|Object} payload - Workspace ID or object with workspaceId and silent flag
         */
        async setCurrentWorkspace({ commit, state }, payload) {
            // Handle both string workspaceId and object payload
            const workspaceId = typeof payload === 'string' ? payload : payload.workspaceId;
            const silent = typeof payload === 'object' ? payload.silent : false;
            
            commit('SET_LOADING', { operation: 'switching', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                // Find workspace in user workspaces first
                let workspace = state.userWorkspaces.find(w => w.id === workspaceId);

                // If not found in user workspaces, fetch it
                if (!workspace) {
                    const response = await WorkspaceService.getWorkspace(workspaceId);
                    workspace = response.data;
                }

                commit('SET_CURRENT_WORKSPACE', workspace);

                // Show success notification only if not silent
                if (!silent) {
                    notify('success', `Switched to ${workspace.name}`);
                }

                return workspace;
            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
                const status = error?.response?.status;
                const message = backendMsg || error.message || 'Unknown error';
                notify('error', `Failed to switch workspace${status ? ` (${status})` : ''}: ${message}`);
                console.error('Error setting current workspace:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'switching', isLoading: false });
            }
        },

        /**
         * Set default workspace (first available)
         *
         * @param {Object} context - Vuex context
         * @param {Object} options - Options object with silent flag
         */
        async setFirstWorkspaceAsDefault({ commit, state, dispatch }, options = {}) {
            const { silent = false } = options;
            
            if (state.userWorkspaces.length > 0) {
                // Set first workspace as current
                const defaultWorkspace = state.userWorkspaces[0];
                commit('SET_CURRENT_WORKSPACE', defaultWorkspace);
                
                // Show notification for default workspace selection only if not silent
                if (!silent) {
                    notify('success', `Switched to ${defaultWorkspace.name}`);
                }
                
                return defaultWorkspace;
            } else {
                // No workspaces available, try to fetch
                try {
                    await dispatch('fetchUserWorkspaces');
                    if (state.userWorkspaces.length > 0) {
                        const defaultWorkspace = state.userWorkspaces[0];
                        commit('SET_CURRENT_WORKSPACE', defaultWorkspace);
                        
                        // Show notification for default workspace selection only if not silent
                        if (!silent) {
                            notify('success', `Switched to ${defaultWorkspace.name}`);
                        }
                        
                        return defaultWorkspace;
                    }
                } catch (error) {
                    console.error('Error setting default workspace:', error);
                }
            }
            return null;
        },

        /**
         * Create a new workspace
         *
         * @param {Object} context - Vuex context
         * @param {Object} workspaceData - Workspace creation data
         */
        async createWorkspace({ commit }, workspaceData) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.createWorkspace(workspaceData);
                const newWorkspace = response.data;

                commit('ADD_WORKSPACE', newWorkspace);

                // Note: Don't automatically set as current workspace or show notification here
                // This will be handled by the calling component

                return newWorkspace;
            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to create workspace: ${error.message}`);
                console.error('Error creating workspace:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        },

        /**
         * Update a workspace
         *
         * @param {Object} context - Vuex context
         * @param {Object} payload - Update payload with workspaceId and data
         */
        async updateWorkspace({ commit }, { workspaceId, data }) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.updateWorkspace(workspaceId, data);
                const updatedWorkspace = response.data;

                commit('UPDATE_WORKSPACE', updatedWorkspace);

                // Show success notification
                notify('success', `Workspace "${updatedWorkspace.name}" updated successfully`);

                return updatedWorkspace;
            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to update workspace: ${error.message}`);
                console.error('Error updating workspace:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        },

        /**
         * Delete a workspace
         *
         * @param {Object} context - Vuex context
         * @param {string} workspaceId - ID of workspace to delete
         */
        async deleteWorkspace({ commit, dispatch, state }, workspaceId) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                await WorkspaceService.deleteWorkspace(workspaceId);

                const workspaceName = state.userWorkspaces.find(w => w.id === workspaceId)?.name || 'Workspace';
                commit('REMOVE_WORKSPACE', workspaceId);

                // If deleted workspace was current, set a new default
                if (!state.currentWorkspace && state.userWorkspaces.length > 0) {
                    await dispatch('setFirstWorkspaceAsDefault');
                }

                // Show success notification
                notify('success', `Workspace "${workspaceName}" deleted successfully`);

            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to delete workspace: ${error.message}`);
                console.error('Error deleting workspace:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        },

        /**
         * Fetch workspace members
         *
         * @param {Object} context - Vuex context
         * @param {string} workspaceId - Workspace ID
         */
        async fetchMembers({ commit }, workspaceId) {
            commit('SET_LOADING', { operation: 'members', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.getWorkspaceMembers(workspaceId);
                commit('SET_MEMBERS', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error);
                console.error('Error fetching workspace members:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'members', isLoading: false });
            }
        },

        /**
         * Add member to workspace
         *
         * @param {Object} context - Vuex context
         * @param {Object} payload - Member data with workspaceId, email, and role
         */
        async addMember({ commit }, { workspaceId, email, role }) {
            commit('SET_LOADING', { operation: 'members', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.addMember(workspaceId, { email, role });
                const newMember = response.data;

                commit('ADD_MEMBER', newMember);

                // Show success notification
                notify('success', `${email} added to workspace successfully`);

                return newMember;
            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to add member: ${error.message}`);
                console.error('Error adding workspace member:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'members', isLoading: false });
            }
        },

        /**
         * Update member role
         *
         * @param {Object} context - Vuex context
         * @param {Object} payload - Update data with workspaceId, userId, and role
         */
        async updateMemberRole({ commit }, { workspaceId, userId, role }) {
            commit('SET_LOADING', { operation: 'members', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.updateMemberRole(workspaceId, userId, { role });
                const updatedMember = response.data;

                commit('UPDATE_MEMBER', updatedMember);

                // Show success notification
                notify('success', 'Member role updated successfully');

                return updatedMember;
            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to update member role: ${error.message}`);
                console.error('Error updating member role:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'members', isLoading: false });
            }
        },

        /**
         * Remove member from workspace
         *
         * @param {Object} context - Vuex context
         * @param {Object} payload - Remove data with workspaceId and userId
         */
        async removeMember({ commit }, { workspaceId, userId }) {
            commit('SET_LOADING', { operation: 'members', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                await WorkspaceService.removeMember(workspaceId, userId);

                commit('REMOVE_MEMBER', userId);

                // Show success notification
                notify('success', 'Member removed from workspace successfully');

            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to remove member: ${error.message}`);
                console.error('Error removing workspace member:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'members', isLoading: false });
            }
        },

        /**
         * Reset workspace state (called on logout)
         *
         * @param {Object} context - Vuex context
         */
        resetWorkspaceState({ commit }) {
            commit('RESET_STATE');
        },

        /**
         * Fetch my workspaces for My Workspaces page
         *
         * @param {Object} context - Vuex context
         * @param {number} currentWorkspaceId - Currently selected workspace ID
         */
        async fetchMyWorkspaces({ commit }, currentWorkspaceId = null) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.getMyWorkspaces(currentWorkspaceId);
                commit('SET_USER_WORKSPACES', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error);
                console.error('Error fetching my workspaces:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        },

        /**
         * Set a workspace as user's default
         *
         * @param {Object} context - Vuex context
         * @param {string} workspaceId - Workspace ID to set as default
         */
        async setDefaultWorkspace({ commit }, workspaceId) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.setDefaultWorkspace(workspaceId);
                const updatedWorkspace = response.data;

                commit('UPDATE_WORKSPACE', updatedWorkspace);

                // Show success notification
                notify('success', `Workspace "${updatedWorkspace.name}" set as default`);

                return updatedWorkspace;
            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to set default workspace: ${error.message}`);
                console.error('Error setting default workspace:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        },

        /**
         * Rename a workspace
         *
         * @param {Object} context - Vuex context
         * @param {Object} payload - Rename payload with workspaceId and newName
         */
        async renameWorkspace({ commit }, { workspaceId, newName }) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.renameWorkspace(workspaceId, newName);
                const updatedWorkspace = response.data;

                commit('UPDATE_WORKSPACE', updatedWorkspace);

                // Show success notification
                notify('success', `Workspace renamed to "${newName}" successfully`);

                return updatedWorkspace;
            } catch (error) {
                commit('SET_ERROR', error);

                // Show error notification
                notify('error', `Failed to rename workspace: ${error.message}`);
                console.error('Error renaming workspace:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        },

        /**
         * Check if user can create more workspaces
         *
         * @param {Object} context - Vuex context
         */
        async checkWorkspaceLimit({ commit }) {
            commit('SET_LOADING', { operation: 'workspaces', isLoading: true });
            commit('CLEAR_ERROR');

            try {
                const response = await WorkspaceService.checkWorkspaceLimit();
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error);
                console.error('Error checking workspace limit:', error);
                throw error;
            } finally {
                commit('SET_LOADING', { operation: 'workspaces', isLoading: false });
            }
        }
    },

    getters: {
        /**
         * Get current workspace
         *
         * @param {Object} state - Current state
         * @returns {Object} Current workspace
         */
        currentWorkspace: state => state.currentWorkspace,

        /**
         * Get current workspace ID
         *
         * @param {Object} state - Current state
         * @returns {string} Current workspace ID
         */
        currentWorkspaceId: state => state.currentWorkspace?.id || null,

        /**
         * Get current workspace name
         *
         * @param {Object} state - Current state
         * @returns {string} Current workspace name
         */
        currentWorkspaceName: state => state.currentWorkspace?.name || '',

        /**
         * Get user's role in current workspace
         *
         * @param {Object} state - Current state
         * @returns {string} User role in current workspace
         */
        currentUserRole: state => state.currentWorkspace?.userRole || null,

        /**
         * Check if current user is workspace owner
         *
         * @param {Object} state - Current state
         * @returns {boolean} True if user is owner
         */
        isCurrentWorkspaceOwner: state => state.currentWorkspace?.userRole === 'OWNER',

        /**
         * Check if current user is workspace admin (owner or admin)
         *
         * @param {Object} state - Current state
         * @returns {boolean} True if user is admin or owner
         */
        isCurrentWorkspaceAdmin: state => {
            const role = state.currentWorkspace?.userRole;
            return role === 'OWNER' || role === 'ADMIN';
        },

        /**
         * Get all user workspaces
         *
         * @param {Object} state - Current state
         * @returns {Array} User workspaces
         */
        userWorkspaces: state => state.userWorkspaces,

        /**
         * Get workspace members
         *
         * @param {Object} state - Current state
         * @returns {Array} Workspace members
         */
        members: state => state.members,

        /**
         * Get member count
         *
         * @param {Object} state - Current state
         * @returns {number} Number of workspace members
         */
        memberCount: state => state.members.length,

        /**
         * Check if any workspace operation is loading
         *
         * @param {Object} state - Current state
         * @returns {boolean} True if any operation is loading
         */
        isLoading: state => Object.values(state.loading).some(loading => loading),

        /**
         * Get specific loading state
         *
         * @param {Object} state - Current state
         * @returns {Function} Function to get loading state by operation
         */
        getLoadingState: state => operation => state.loading[operation] || false,

        /**
         * Get current error
         *
         * @param {Object} state - Current state
         * @returns {Error} Current error
         */
        error: state => state.error,

        /**
         * Get last updated timestamp
         *
         * @param {Object} state - Current state
         * @returns {Date} Last updated timestamp
         */
        lastUpdated: state => state.lastUpdated,

        /**
         * Check if user has any workspaces
         *
         * @param {Object} state - Current state
         * @returns {boolean} True if user has workspaces
         */
        hasWorkspaces: state => state.userWorkspaces.length > 0,

        /**
         * Get workspace by ID
         *
         * @param {Object} state - Current state
         * @returns {Function} Function to get workspace by ID
         */
        getWorkspaceById: state => workspaceId => {
            return state.userWorkspaces.find(workspace => workspace.id === workspaceId);
        },

        /**
         * Get member by user ID
         *
         * @param {Object} state - Current state
         * @returns {Function} Function to get member by user ID
         */
        getMemberByUserId: state => userId => {
            return state.members.find(member => member.userId === userId);
        }
    }
};

export default workspace;